// @flow

import { Component } from '../jsti/core';
import Notifications from '../constants/Notifications';
import { isLargeScreen } from '../classes/Helpers';

export default class TabAccordion extends Component {
  #index: number = 0;
  #activeIndex: number | null = 0;
  #header: HTMLElement;
  #body: HTMLElement;

  /**
   * Initiate the component
   */
  bind(pEl: HTMLElement): void {
    super.bind(pEl);
    this.setUp(pEl);

    this.addListener(Notifications.ACTIVATE_TAB, this.handleActivation);
    this.addListener(Notifications.RESIZE, this.handleResize);

    if (this.#activeIndex === this.#index) {
      this.sendNotification();
    }
  }

  /**
   * Setup properties and validation
   */
  setUp(pEl: HTMLElement): void {
    this.#index = parseInt(pEl.dataset.index);

    const header: HTMLElement | null = pEl.querySelector('[ref="header"]');
    const body: HTMLElement | null = pEl.querySelector('[ref="body"]');

    if (Number.isNaN(this.#index)) {
      throw new Error('A index is required');
    }

    if (header && body) {
      this.#header = header;
      this.#body = body;
    } else {
      throw new Error('Both the tab header and body need to exist.');
    }
  }

  /**
   * Executes when the component is loaded
   */
  loaded(): void {
    if (this.isActive()) {
      this.sendNotification();
    }
  }

  /**
   * Executes when the correct notification is received
   */
  handleActivation(pData: Object): void {
    if (this.#activeIndex !== pData.index) {
      this.#activeIndex = pData.index;
    } else if (!isLargeScreen()) {
      this.#activeIndex = null;
    }

    if (this.isActive()) {
      this.addClass();
      this.sendNotification();
    } else {
      this.removeClass();
    }
  }

  /**
   * Executes when the viewport is resized
   */
  handleResize(): void {
    if (isLargeScreen() && this.#activeIndex === null) {
      this.handleActivation({ index: 0 });
    }

    if (this.isActive()) {
      this.sendNotification();
    }
  }

  /**
   * Send a notification
   */
  sendNotification(): void {
    const height = this.calculateHeight();
    this.notify(Notifications.CHANGE_TAB, { height: height });
  }

  /**
   * Add an active class
   */
  addClass(): void {
    this.el.classList.add('tab--active');
  }

  /**
   * Remove the active class
   */
  removeClass(): void {
    this.el.classList.remove('tab--active');
  }

  /**
   * Calculate the height of the accordion
   */
  calculateHeight(): string {
    if (isLargeScreen()) {
      return this.#header.offsetHeight + this.#body.offsetHeight + 'px';
    } else {
      return 'initial';
    }
  }

  /**
   * Check if the accordion is active
   */
  isActive(): boolean {
    return this.#index === this.#activeIndex;
  }
}
